import moment from 'moment';
import { all, call, put, select } from 'redux-saga/effects';
import { getFields, getFieldsForType, getFieldsInitiated } from '../selectors/fields';

import { Fields, Logger, Reports } from '../services';
import reportConfigs from '../reducers/reportsConfig';
import { fetchedFields, updatedField, updatedFields, updatedFilters } from '../actions/fields';
import {
    clearReportFilters,
    dateChanged,
    disableReportStateChanged,
    reportsFieldClickedBulk,
    setActionType,
} from '../actions/reports';
import { getUserData } from '../selectors/user';
import { getDefaultCompareDates } from '../utils/regional';
import fieldVisibilityRules from '../selectors/reportsConfig/fieldVisibilityRules';
import fieldDefaultRules from '../selectors/reportsConfig/fieldDefaultingRules';
import { REGULAR_MODE } from '../components/pages/reports/consts';
import { normalizeSourceDisplayName } from '../utils/adNetworks';
import { isObjectEmpty } from '../utils/general';
import { getDimensionAvailabilityTypesWhitelist } from '../fieldsAvailability/utils';
import { getIsAdvanceMode, getSelectedSources } from '../selectors/reports';
import { reportTypes, splitCreativesAIDimensions } from '../utils/reports';

const fieldsAPI = new Fields();
const reportsAPI = new Reports();
const log = new Logger('[sagas][fields]');

const getReportShelfFieldTypes = (reportType, reportMode) => {
    const reportConfig = reportConfigs.find(config => config.type === reportType);
    const reportConfigFieldsShelf = reportConfig.shelf[reportMode] || reportConfig.shelf;
    return reportConfigFieldsShelf.map(shelf => shelf.type);
};

function* updateReportFields(fields, reportType, reportMode = REGULAR_MODE) {
    const userData = yield select(getUserData);
    const validFieldTypes = getReportShelfFieldTypes(reportType, reportMode);
    const selectedSources = yield select(getSelectedSources(reportType));
    const isAdvanceMode = yield select(getIsAdvanceMode(reportType));

    const defaultRules = fieldDefaultRules[reportType];
    const visibilityRules = fieldVisibilityRules[reportType];

    Object.keys(fields).forEach(fieldCategory => {
        if (!fields[fieldCategory]) {
            fields[fieldCategory] = [];
            return;
        }

        const filteredFields = fields[fieldCategory].filter(field => {
            if (!validFieldTypes.includes(field.actual_type)) return false;

            const isDefault = defaultRules(field, userData.adminMode, reportMode, selectedSources);

            if (!isDefault) return false;

            const isVisible = visibilityRules(
                field,
                userData.adminMode,
                userData,
                false,
                isAdvanceMode,
                selectedSources
            );

            return isVisible || fieldDefaultRules.force(field);
        });

        fields[fieldCategory] = filteredFields.map(({ name }) => name);
    });

    yield put(setActionType(reportsFieldClickedBulk(fields), reportType));
}

export function* updateAllFields(force) {
    try {
        const { value: allFields } = yield call(fieldsAPI.updateAllFields, force);
        yield put(updatedFields(allFields));
    } catch (error) {
        log.error('Error: Failed to update all fields');
    }
}

export function* updateCreativesAIDimensions(reportType) {
    try {
        if (reportType && reportType !== reportTypes.creativeExplore) return;

        const { value: allCreativesAIDimensions } = yield call(fieldsAPI.updateCreativesAIDimensionsFields);

        const { creativesAIDimensions, creativesAICustomDimensions } = splitCreativesAIDimensions(
            allCreativesAIDimensions
        );

        yield put(updatedField('creativesAIDimensions', creativesAIDimensions));
        yield put(updatedField('creativesAICustomDimensions', creativesAICustomDimensions));
    } catch (error) {
        log.error('Error: Failed to update creatives AI dimensions');
    }
}

export function* updateFilterFields(reportType) {
    try {
        const { value: filterFields } = yield call(fieldsAPI.updateFilterFields, reportType);
        yield put(updatedFilters(filterFields));
    } catch (error) {
        log.error('Error: Failed to update filter fields');
    }
}

export function* updateSourcesFieldsAvailability(reportType) {
    try {
        const { value: fieldsAvailabilityByDimension } = yield call(fieldsAPI.updateSourcesFieldsAvailability);

        if (isObjectEmpty(fieldsAvailabilityByDimension)) return;

        const allFields = yield select(getFields);

        for (const fieldType of getDimensionAvailabilityTypesWhitelist(reportType)) {
            const relevantFields = allFields[fieldType];

            if (!relevantFields) {
                continue;
            }

            const updatedFieldsList = relevantFields.map(field => {
                const dimensionName = field.name.toLowerCase();
                const dimensionAvailableSources = fieldsAvailabilityByDimension[dimensionName];
                const sources = (dimensionAvailableSources || []).map(normalizeSourceDisplayName);

                return {
                    ...field,
                    available_sources: sources,
                };
            });

            yield put(updatedField(fieldType, updatedFieldsList));
        }
    } catch (error) {
        log.error('Error: Failed to update fields availability available sources');
    }
}

function* updateBookmarks() {
    try {
        const response = yield call(fieldsAPI.updateBookmarks, {});
        yield put(updatedField('bookmarks', response.bookmarks));
    } catch (error) {
        log.error('Error: failed to update bookmarks');
    }
}

function* updateReportInitState(reportType) {
    try {
        const response = yield call(reportsAPI.updateReportInitState, {});
        const { disable_report_state: disableReportState } = response;

        yield put(setActionType(disableReportStateChanged(disableReportState), reportType));
    } catch (error) {
        log.error('Error: failed to update report init state');
    }
}

function* updateReportDates(reportType) {
    try {
        const response = yield call(reportsAPI.updateReportDates, {});
        yield put(updatedField('dates', response));
        const { end_date } = response;
        let { start_date } = response;

        const { defaultDaysBack } = reportConfigs.find(config => config.type === reportType) || {};
        if (defaultDaysBack) {
            start_date = moment(end_date)
                .subtract(defaultDaysBack, 'days')
                .format('YYYY-MM-DD');
        }

        const dates = {
            end_date,
            start_date,
            ...getDefaultCompareDates(start_date, end_date),
        };
        yield put(setActionType(dateChanged(dates), reportType));
    } catch (error) {
        log.error('Error: failed to update report dates');
    }
}

function* updateFiltersDefault(reportType) {
    yield put(setActionType(clearReportFilters(), reportType));
}

function* updateGoalsList() {
    try {
        const response = yield call(fieldsAPI.updateGoalsList, {});
        yield put(updatedField('goals', response));
    } catch (error) {
        log.error('Error: failed to update goals metrics');
    }
}

export function* getFieldsDict() {
    const fieldList = [
        'dimensions',
        'governanceDimensions',
        'creativesAIDimensions',
        'creativesAICustomDimensions',
        'custom_dimensions',
        'skan_redownloads_dimensions',
        'skan_validated_dimensions',
        'skanCustomEvents',
        'confidenceIntervalFlag',
        'skanConversionDimensions',
        'periods',
        'cohortMetrics',
        'xorgCohortMetrics',
        'metrics',
        'xorgMetrics',
        'appMetrics',
        'webMetrics',
        'utmParamsFields',
        'crossDeviceMetrics',
        'discrepancyMetrics',
        'dataTypeFields',
        'metricGroupFields',
        'cohortMetricGroupFields',
        'goalsMetrics',
        'goalsForecastMetrics',
        'skanModeledMetrics',
        'skanModeledCohortMetrics',
        'skanModeledCohortPeriods',
        'unifiedCohortPeriods',
        'customEvents',
        'timeBreakdowns',
        'creativeShowPreview',
        'skanDateDimensionName',
        'sourceAttributionType',
        'withDruidAppend',
        'druidAggregatedDataSourcesMode',
        'crossDeviceDemoCohortType',
        'creativeMeasurementDimensions',
        'promotedContentDetailsDimensions',
        'crossDeviceCohortType',
        'adMonDAUMetrics',
        'socialEngagementMetrics',
        'assistMetrics',
        'technicalSpecsDimensions',
        'trackerData',
        'networkData',
        'discrepancies',
        'videoMetrics',
    ];

    const fieldsDict = {};
    for (const field of fieldList) {
        fieldsDict[field] = yield select(getFieldsForType(field));
    }

    return fieldsDict;
}

function* updateReportsPageDefaults(reportType) {
    const fieldsDict = yield call(getFieldsDict);

    yield call(updateReportInitState, reportType);
    yield call(updateReportDates, reportType);
    yield call(updateReportFields, fieldsDict, reportType);
    yield call(updateFiltersDefault, reportType);
}

function* updateFieldsHandler(reportType, selectDefaults = false, force = false) {
    try {
        const initiated = yield select(getFieldsInitiated);

        if (!initiated || force) {
            yield all([call(updateBookmarks), call(updateAllFields, force), call(updateGoalsList)]);
        }

        if (selectDefaults) {
            yield call(updateReportsPageDefaults, reportType);
        }

        yield put(fetchedFields());
    } catch (e) {
        console.log(e);
    }
}

function* updateFilterFieldsHandler(reportType) {
    try {
        yield call(updateFilterFields, reportType);
    } catch (e) {
        console.log(e);
    }
}

export { updateFieldsHandler, updateFilterFieldsHandler, updateReportFields };
